import React, { useState, useCallback, useEffect } from 'react';
import {
  Card,
  Page,
  FormLayout,
  TextField,
  Button,
  DropZone,
  LegacyStack,
  Thumbnail,
  Banner,
  Spinner,
  InlineStack,
  Text,
  Icon,
  Tooltip,
  DataTable,
  ButtonGroup,
  Checkbox,
  ProgressBar
} from '@shopify/polaris';
import {
  ImageMajor,
  DeleteMinor,
  DuplicateMinor,
  CircleTickMinor
} from '@shopify/polaris-icons';
import { BlobServiceClient } from '../services/blobStorageCompat';
import { authService } from '../services/authService';
import { getServerURL } from '../services/config';

const SERVER_URL = getServerURL();

const DataEntryPage = ({ setToastMessage, setToastActive }) => {
  const [orderId, setOrderId] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [azureSettings, setAzureSettings] = useState(null);
  const [copiedUrls, setCopiedUrls] = useState(new Set());
  const [validationError, setValidationError] = useState('');
  
  // Face detection states
  const [enableFaceDetection, setEnableFaceDetection] = useState(true);
  const [enableCrop, setEnableCrop] = useState(true);
  const [faceApiLoaded, setFaceApiLoaded] = useState(false);
  const [isLoadingFaceApi, setIsLoadingFaceApi] = useState(false);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [faceCropMarginFactor, setFaceCropMarginFactor] = useState(0.4);
  const [fileErrors, setFileErrors] = useState(new Map()); // Track errors for each file

  // Load Azure settings from API
  const loadAzureSettings = useCallback(async () => {
    try {
      const response = await fetch(`${SERVER_URL}/settings/storage/client`, {
        headers: authService.getAuthHeaders(),
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data && result.data.length > 0) {
          setAzureSettings(result.data[0]);
        }
      } else {
        console.error('Failed to load Azure settings:', response.status);
        setToastMessage('Failed to load Azure settings');
        setToastActive(true);
      }
    } catch (error) {
      console.error('Error loading Azure settings:', error);
      setToastMessage('Error loading Azure settings');
      setToastActive(true);
    }
  }, [setToastMessage, setToastActive]);

  useEffect(() => {
    loadAzureSettings();
  }, [loadAzureSettings]);

  // Face API initialization
  const ensureFaceApi = useCallback(async () => {
    if (faceApiLoaded) return true;
    
    setIsLoadingFaceApi(true);
    try {
      // Load face-api.js from CDN if not already loaded
      if (!window.faceapi) {
        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/face-api.js@0.22.2/dist/face-api.min.js';
        script.async = true;
        
        await new Promise((resolve, reject) => {
          script.onload = resolve;
          script.onerror = reject;
          document.head.appendChild(script);
        });
      }

      // Load models
      const modelUrl = 'https://raw.githubusercontent.com/justadudewhohacks/face-api.js/master/weights';
      await Promise.all([
        window.faceapi.nets.tinyFaceDetector.loadFromUri(modelUrl),
        window.faceapi.nets.faceLandmark68Net.loadFromUri(modelUrl),
        window.faceapi.nets.faceRecognitionNet.loadFromUri(modelUrl),
        window.faceapi.nets.ageGenderNet.loadFromUri(modelUrl)
      ]);

      setFaceApiLoaded(true);
      return true;
    } catch (error) {
      console.error('Failed to load face-api.js:', error);
      setToastMessage('Failed to load face detection models');
      setToastActive(true);
      return false;
    } finally {
      setIsLoadingFaceApi(false);
    }
  }, [faceApiLoaded, setToastMessage, setToastActive]);

  // Face validation function
  const validateFace = useCallback(async (imageElement) => {
    try {
      const detections = await window.faceapi
        .detectAllFaces(imageElement, new window.faceapi.TinyFaceDetectorOptions())
        .withFaceLandmarks()
        .withAgeAndGender();

      if (detections.length === 0) {
        throw new Error('No face detected in the image. Please upload an image with a clear face.');
      }

      if (detections.length > 1) {
        throw new Error('Multiple faces detected. Please upload an image with only one face.');
      }

      const detection = detections[0];
      const confidence = detection.detection.score;

      if (confidence < 0.4) {
        throw new Error('Face detection confidence too low. Please upload a clearer image.');
      }

      return {
        age: Math.round(detection.age),
        gender: detection.gender,
        confidence: confidence,
        faceBox: detection.detection.box
      };
    } catch (error) {
      throw error;
    }
  }, []);

  // Face cropping function
  const cropImageAroundFace = useCallback(async (imageElement, faceBox) => {
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      if (!ctx) {
        throw new Error('Failed to get canvas context');
      }

      const marginFactor = faceCropMarginFactor;
      const faceWidth = faceBox.width;
      const faceHeight = faceBox.height;
      
      // Calculate margins
      const marginX = faceWidth * marginFactor;
      const marginY = faceHeight * marginFactor;
      
      // Calculate crop dimensions with proper bounds checking
      const cropX = Math.max(0, faceBox.x - marginX * 0.8);
      const cropY = Math.max(0, faceBox.y - marginY * 1.5); // More space above for hair
      const cropWidth = Math.min(imageElement.width - cropX, faceWidth + (marginX * 1.6));
      const cropHeight = Math.min(imageElement.height - cropY, faceHeight + (marginY * 2.3));
      
      // Set canvas dimensions
      canvas.width = cropWidth;
      canvas.height = cropHeight;
      
      // Draw cropped image
      ctx.drawImage(
        imageElement,
        cropX, cropY, cropWidth, cropHeight,
        0, 0, cropWidth, cropHeight
      );
      
      // Convert to blob
      return new Promise((resolve) => {
        canvas.toBlob(resolve, 'image/jpeg', 0.9);
      });
    } catch (error) {
      console.error('Face cropping failed:', error);
      throw error;
    }
  }, [faceCropMarginFactor]);

  // Process single file with face detection
  const processSingleFile = useCallback(async (file, index, totalFiles) => {
    try {
      setProcessingProgress((index / totalFiles) * 100);
      
      // Clear any previous error for this file
      setFileErrors(prevErrors => {
        const newErrors = new Map(prevErrors);
        newErrors.delete(file.name);
        return newErrors;
      });
      
      if (!enableFaceDetection) {
        return file;
      }

      // Ensure face API is loaded
      const apiLoaded = await ensureFaceApi();
      if (!apiLoaded) {
        const error = 'Face detection API not available';
        setFileErrors(prevErrors => new Map(prevErrors).set(file.name, error));
        throw new Error(error);
      }

      // Create image element
      const imageElement = new Image();
      const imageUrl = URL.createObjectURL(file);
      
      await new Promise((resolve, reject) => {
        imageElement.onload = resolve;
        imageElement.onerror = reject;
        imageElement.src = imageUrl;
      });

      try {
        // Validate face
        const faceData = await validateFace(imageElement);
        
        // Crop if enabled
        if (enableCrop) {
          try {
            const croppedBlob = await cropImageAroundFace(imageElement, faceData.faceBox);
            if (croppedBlob) {
              // Create new file from cropped blob
              const croppedFile = new File([croppedBlob], file.name, {
                type: 'image/jpeg',
                lastModified: Date.now()
              });
              return croppedFile;
            }
          } catch (cropError) {
            console.warn('Cropping failed, using original image:', cropError);
            const errorMsg = `Cropping failed: ${cropError.message}`;
            setFileErrors(prevErrors => new Map(prevErrors).set(file.name, errorMsg));
            setToastMessage(`Cropping failed for ${file.name}, using original image`);
            setToastActive(true);
          }
        }
        
        return file;
      } finally {
        URL.revokeObjectURL(imageUrl);
      }
    } catch (error) {
      console.error(`Face processing failed for ${file.name}:`, error);
      // Store the error for this file
      setFileErrors(prevErrors => new Map(prevErrors).set(file.name, error.message));
      throw error;
    }
  }, [enableFaceDetection, enableCrop, ensureFaceApi, validateFace, cropImageAroundFace, setToastMessage, setToastActive]);

  // Upload file to Azure Blob with order ID directory structure
  const uploadFileToAzure = useCallback(async (file, orderIdValue) => {
    if (!azureSettings) {
      throw new Error('Azure settings not loaded');
    }
    
    const { AZURE_BLOB_BASE_URL, AZURE_SAS_TOKEN } = azureSettings;
    const containerUrl = AZURE_BLOB_BASE_URL + AZURE_SAS_TOKEN;
    const blobServiceClient = new BlobServiceClient(containerUrl);
    const containerClient = blobServiceClient.getContainerClient("");
    
    // Sanitize order ID and create directory structure
    const sanitizeOrderId = (str) => (str || '').replace(/[^a-zA-Z0-9-_]/g, '_');
    const sanitizedOrderId = sanitizeOrderId(orderIdValue);
    
    // Get file extension
    const ext = file.name.split('.').pop();
    
    // Generate unique filename with timestamp
    const timestamp = Date.now();
    const randomString = Math.random().toString(36).substring(2, 8);
    const fileName = `${timestamp}_${randomString}.${ext}`;
    
    // Create blob path with order ID directory
    const blobName = `data-entry/${sanitizedOrderId}/${fileName}`;
    
    const blockBlobClient = containerClient.getBlockBlobClient(blobName);
    
    const { publicUrl } = await blockBlobClient.uploadBrowserData(file, {
      blobHTTPHeaders: { blobContentType: file.type }
    });
    
    return publicUrl;
  }, [azureSettings]);

  // Handle file selection
  const handleDropZoneDrop = useCallback((files) => {
    setSelectedFiles(files);
    setValidationError('');
    setFileErrors(new Map()); // Clear previous file errors
  }, []);

  // Remove selected file
  const removeFile = useCallback((index) => {
    setSelectedFiles(files => {
      const newFiles = files.filter((_, i) => i !== index);
      // Remove error for this file
      setFileErrors(prevErrors => {
        const newErrors = new Map(prevErrors);
        newErrors.delete(files[index]?.name);
        return newErrors;
      });
      return newFiles;
    });
  }, []);

  // Validate form
  const validateForm = () => {
    if (!orderId.trim()) {
      setValidationError('Order ID is required');
      return false;
    }
    if (selectedFiles.length === 0) {
      setValidationError('Please select at least one image');
      return false;
    }
    setValidationError('');
    return true;
  };

  // Handle upload
  const handleUpload = useCallback(async () => {
    if (!validateForm()) {
      return;
    }

    if (!azureSettings) {
      setToastMessage('Azure settings not loaded. Please try again.');
      setToastActive(true);
      return;
    }

    setIsUploading(true);
    setProcessingProgress(0);
    const uploadResults = [];
    
    try {
      // Process files with face detection if enabled
      const processedFiles = [];
      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i];
        try {
          const processedFile = await processSingleFile(file, i, selectedFiles.length);
          processedFiles.push(processedFile);
        } catch (error) {
          console.error(`Face processing failed for ${file.name}:`, error);
          if (enableFaceDetection) {
            setToastMessage(`Face processing failed for ${file.name}: ${error.message}`);
            setToastActive(true);
            continue; // Skip this file if face detection is required
          } else {
            processedFiles.push(file); // Use original file if face detection is not required
          }
        }
      }

      setProcessingProgress(100);

      // Upload processed files
      for (let i = 0; i < processedFiles.length; i++) {
        const file = processedFiles[i];
        try {
          const url = await uploadFileToAzure(file, orderId);
          uploadResults.push({
            fileName: file.name,
            url: url,
            orderId: orderId,
            uploadedAt: new Date().toISOString(),
            processed: enableFaceDetection,
            cropped: enableFaceDetection && enableCrop
          });
        } catch (error) {
          console.error(`Failed to upload ${file.name}:`, error);
          setToastMessage(`Failed to upload ${file.name}`);
          setToastActive(true);
        }
      }
      
      if (uploadResults.length > 0) {
        setUploadedImages(prev => [...uploadResults, ...prev]);
        setSelectedFiles([]);
        setOrderId('');
        const message = enableFaceDetection 
          ? `Successfully processed and uploaded ${uploadResults.length} image(s)${enableCrop ? ' with face cropping' : ''}`
          : `Successfully uploaded ${uploadResults.length} image(s)`;
        setToastMessage(message);
        setToastActive(true);
      }
    } catch (error) {
      console.error('Upload error:', error);
      setToastMessage('Upload failed. Please try again.');
      setToastActive(true);
    } finally {
      setIsUploading(false);
      setProcessingProgress(0);
    }
  }, [selectedFiles, orderId, azureSettings, uploadFileToAzure, processSingleFile, enableFaceDetection, enableCrop, setToastMessage, setToastActive]);

  // Copy URL to clipboard
  const copyToClipboard = useCallback(async (url, fileName) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedUrls(prev => new Set([...prev, url]));
      setToastMessage(`URL copied for ${fileName}`);
      setToastActive(true);
      
      // Remove from copied set after 3 seconds
      setTimeout(() => {
        setCopiedUrls(prev => {
          const newSet = new Set(prev);
          newSet.delete(url);
          return newSet;
        });
      }, 3000);
    } catch (error) {
      console.error('Failed to copy URL:', error);
      setToastMessage('Failed to copy URL');
      setToastActive(true);
    }
  }, [setToastMessage, setToastActive]);

  // Clear all uploaded images
  const clearUploadedImages = useCallback(() => {
    setUploadedImages([]);
    setCopiedUrls(new Set());
    setToastMessage('Cleared all uploaded images');
    setToastActive(true);
  }, [setToastMessage, setToastActive]);

  // Prepare data for DataTable
  const tableRows = uploadedImages.map((image, index) => [
    image.fileName,
    image.orderId,
    new Date(image.uploadedAt).toLocaleString(),
    image.processed ? (image.cropped ? 'Face Detected & Cropped' : 'Face Detected') : 'No Processing',
    <ButtonGroup key={index}>
      <Tooltip content={copiedUrls.has(image.url) ? 'Copied!' : 'Copy URL'}>
        <Button
          icon={copiedUrls.has(image.url) ? CircleTickMinor : DuplicateMinor}
          onClick={() => copyToClipboard(image.url, image.fileName)}
          size="slim"
          variant={copiedUrls.has(image.url) ? 'primary' : 'secondary'}
        />
      </Tooltip>
    </ButtonGroup>
  ]);

  const fileUpload = !selectedFiles.length && (
    <DropZone.FileUpload actionHint="or drop files to upload" allowMultiple={true} actionTitle='Upload images' />
  );

  const uploadedFilesList = selectedFiles.length > 0 && (
    <LegacyStack vertical>
      {selectedFiles.map((file, index) => {
        const fileError = fileErrors.get(file.name);
        return (
          <LegacyStack alignment="center" key={index}>
            <Thumbnail
              size="small"
              alt={file.name}
              source={URL.createObjectURL(file)}
            />
            <div style={{ flex: 1 }}>
              <Text variant="bodyMd" as="p">
                {file.name}
              </Text>
              <Text variant="bodySm" color="subdued">{(file.size / 1024 / 1024).toFixed(2)} MB</Text>
              {fileError && (
                <div style={{ marginTop: '4px' }}>
                  <Text variant="bodySm" color="critical">
                    ⚠️ {fileError}
                  </Text>
                </div>
              )}
            </div>
            <Button
              icon={DeleteMinor}
              onClick={() => removeFile(index)}
              variant="tertiary"
              size="slim"
            />
          </LegacyStack>
        );
      })}
    </LegacyStack>
  );

  return (
   
    <LegacyStack vertical spacing="loose">
      {validationError && (
        <Banner status="critical">
          {validationError}
        </Banner>
      )}
      
      <Card sectioned>
        <FormLayout>
          <TextField
            label="Order ID"
            value={orderId}
            onChange={setOrderId}
            placeholder="Enter order ID"
            helpText="Images will be organized by this order ID in Azure storage"
            disabled={isUploading}
          />
          
          {/* Face Detection Settings */}
          <LegacyStack vertical>
            <Text variant="headingMd" as="h3">
              Select Images
            </Text>
            <DropZone
              onDrop={handleDropZoneDrop}
              accept="image/*"
              type="image"
              allowMultiple
              disabled={isUploading}
            >
              {uploadedFilesList}
              {fileUpload}
            </DropZone>
          </LegacyStack>

          {/* Processing Progress */}
          {isUploading && processingProgress > 0 && processingProgress < 100 && (
            <LegacyStack vertical spacing="tight">
              <Text variant="bodyMd">Processing images with face detection...</Text>
              <ProgressBar progress={processingProgress} />
            </LegacyStack>
          )}
          
          <InlineStack align="end">
            <Button
              primary
              onClick={handleUpload}
              loading={isUploading}
              disabled={!orderId.trim() || selectedFiles.length === 0}
            >
              {isUploading ? 'Processing & Uploading...' : `Upload ${selectedFiles.length} Image(s)`}
            </Button>
          </InlineStack>
        </FormLayout>
      </Card>

      {uploadedImages.length > 0 && (
        <Card>
          <div style={{ padding: '16px' }}>
            <InlineStack align="space-between">
              <Text variant="headingMd" as="h3">
                Uploaded Images ({uploadedImages.length})
              </Text>
              <Button
                variant="tertiary"
                onClick={clearUploadedImages}
                size="slim"
              >
                Clear All
              </Button>
            </InlineStack>
          </div>
          
          <DataTable
            columnContentTypes={['text', 'text', 'text', 'text', 'text']}
            headings={['File Name', 'Order ID', 'Uploaded At', 'Processing Status', 'Actions']}
            rows={tableRows}
          />
        </Card>
      )}
    </LegacyStack>
  );
};

export default DataEntryPage;